<template>
	<div class="animated fadeIn">
		<template
			v-if="hasData && !loading">
			<!-- Update phone number -->
			<b-card
				v-if="requiredPhone"
				header-bg-variant="dark"
				class="mb-2">
				<template
					#header>
					<span class="pt-1">
						{{ translate('mobile_number') }}
					</span>
					<button
						class="btn btn-sm btn-success float-right"
						@click="goToDetails()">
						{{ translate('order_details') }}
						<span class="fa fa-eye" />
					</button>
				</template>
				<b-row>
					<b-col cols="4">
						<p>
							<span class="text-muted">
								<strong v-if="requiredPhone">
									{{ translate('value_registered_in_order', { value: translate('mobile_number') }) }}:
								</strong>
								<strong v-else>
									{{ translate('order_data_has_changed') }}:
								</strong>
								<br>
								{{ getOrderValue('telephone') }}
							</span>
						</p>
					</b-col>
					<b-col>
						<label for="telephone">
							<template v-if="requiredPhone">
								{{ translate('provide_new_value', { value: translate('mobile_number')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('mobile_number') }) }}:
								</strong>
							</template>
						</label>
						<vue-tel-input
							ref="mobileNumberInput"
							v-model="form.mobile_number"
							:aria-disabled="!isPhoneEditable"
							:disabled="!isPhoneEditable"
							:placeholder="translate('enter_phone_number')"
							v-bind="bindProps"
							:class="hasError('mobile_number') ? 'is-invalid' : ''"
							name="telephone"
							class="form-control rounded-1"
							:only-countries="['CN']"
							default-country="CN"
							:dropdown-options="{disabled: true}"
							@input="clearError('mobile_number')" />
						<template v-if="hasError('mobile_number')">
							<span
								v-for="error in errors['mobile_number']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
				</b-row>
			</b-card>
			<!-- Update recipient id -->
			<b-card
				v-if="requiredRecipientId"
				header-bg-variant="dark"
				class="mb-2">
				<template
					#header>
					<span class="pt-1">
						{{ translate('id_number') }}
					</span>
					<button
						v-if="!requiredPhone"
						class="btn btn-sm btn-success float-right"
						@click="goToDetails()">
						{{ translate('order_details') }}
						<span class="fa fa-eye" />
					</button>
				</template>
				<b-row>
					<b-col
						v-if="!isIdEditable"
						cols="12">
						<p>
							<span class="text-muted">
								<strong>
									{{ translate('order_data_has_changed') }}:
								</strong>
								<br>
								{{ getOrderValue('recipient_id_number') }} {{ getOrderValue('first_name') }} {{ getOrderValue('last_name') }}
							</span>
						</p>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isIdEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('id_number') }) }}:
								</strong>
								<br>
								{{ getOrderValue('recipient_id_number') }}
							</span>
						</p>
						<label for="recipient_id">
							<template v-if="isIdEditable">
								{{ translate('provide_new_value', { value: translate('id_number')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('id_number') }) }}:
								</strong>
							</template>
						</label>
						<b-input
							id="recipient_id"
							v-model="form.personal_document_number"
							:disabled="!isIdEditable"
							:placeholder="translate('id_number')"
							:class="hasError('personal_document_number') ? 'is-invalid' : ''"
							@input="clearError('personal_document_number')" />
						<template v-if="hasError('personal_document_number')">
							<span
								v-for="error in errors['personal_document_number']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isIdEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('first_name') }) }}:
								</strong>
								<br>
								{{ getOrderValue('first_name') }}
							</span>
						</p>
						<label for="first_name">
							<template v-if="isIdEditable">
								{{ translate('provide_new_value', { value: translate('first_name')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('first_name') }) }}:
								</strong>
							</template>
						</label>
						<b-input
							id="first_name"
							v-model="form.first_name"
							:disabled="!isIdEditable"
							:placeholder="translate('first_name')"
							:class="hasError('first_name') ? 'is-invalid' : ''"
							@input="clearError('first_name')" />
						<template v-if="hasError('first_name')">
							<span
								v-for="error in errors['first_name']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isIdEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('last_name') }) }}:
								</strong>
								<br>
								{{ getOrderValue('last_name') }}
							</span>
						</p>
						<label for="last_name">
							<template v-if="isIdEditable">
								{{ translate('provide_new_value', { value: translate('last_name')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('last_name') }) }}:
								</strong>
							</template>
						</label>
						<b-input
							id="last_name"
							v-model="form.last_name"
							:disabled="!isIdEditable"
							:placeholder="translate('last_name')"
							:class="hasError('last_name') ? 'is-invalid' : ''"
							@input="clearError('last_name')" />
						<template v-if="hasError('last_name')">
							<span
								v-for="error in errors['last_name']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
				</b-row>
				<b-row class="mt-3">
					<b-col
						cols="6"
						class="my-2">
						<label for="personal_document_front_file">
							{{ translate(`nin_${countryCode.toLowerCase()}_front`) }}
						</label>
						<template v-if="isIdEditable">
							<b-form-file
								id="personal_document_front_file"
								v-model="newFrontFile"
								:placeholder="translate('no_file')"
								:browse-text="translate('browse')"
								:accept="IMAGES_EXT.map((ext) => `.${ext}`).join(', ')"
								class="text-truncate"
								:class="hasError('personal_document_front_file') ? 'is-invalid' : ''"
								type="file"
								@change="clearError('personal_document_front_file')" />
							<b-form-text id="personal_document_front_file">
								{{ translate('max', {size: '5'}) }} {{ translate('allowed_ext', {ext: IMAGES_EXT }) }}
							</b-form-text>
							<template v-if="hasError('personal_document_front_file')">
								<span
									v-for="error in errors['personal_document_front_file']"
									:key="error"
									class="custom-invalid-feedback animated fadeIn"
									v-text="error" />
							</template>
						</template>
						<br>
						<img
							v-if="userImageFront"
							:src="userImageFront || require('@/assets/images/common/notFoundAvatar.png')"
							width="100%"
							alt="Front side"
							@click="currentImage = userImageFront; showImageViewer = true;">
					</b-col>
					<b-col
						cols="6"
						class="my-2">
						<label for="personal_document_back_file">
							{{ translate(`nin_${countryCode.toLowerCase()}_back`) }}
						</label>
						<template v-if="isIdEditable">
							<b-form-file
								id="personal_document_back_file"
								v-model="newBackFile"
								:placeholder="translate('no_file')"
								:browse-text="translate('browse')"
								:accept="IMAGES_EXT.map((ext) => `.${ext}`).join(', ')"
								class="text-truncate"
								:class="hasError('personal_document_back_file') ? 'is-invalid' : ''"
								type="file"
								@change="clearError('personal_document_back_file')" />
							<b-form-text id="personal_document_back_file">
								{{ translate('max', {size: '5'}) }} {{ translate('allowed_ext', {ext: IMAGES_EXT }) }}
							</b-form-text>
							<template v-if="hasError('personal_document_back_file')">
								<span
									v-for="error in errors['personal_document_back_file']"
									:key="error"
									class="custom-invalid-feedback animated fadeIn"
									v-text="error" />
							</template>
						</template>
						<br>
						<img
							v-if="userImageBack"
							width="100%"
							:src="userImageBack || require('@/assets/images/common/notFoundAvatar.png')"
							alt="Back side"
							@click="currentImage = userImageBack; showImageViewer = true;">
						<image-viewer
							v-model="showImageViewer"
							:image-src="currentImage || require('@/assets/images/common/notFoundAvatar.png')" />
					</b-col>
				</b-row>
			</b-card>
			<!-- Update address -->
			<b-card
				v-if="requiredAddress"
				header-bg-variant="dark"
				class="mb-2">
				<template
					#header>
					<span class="pt-1">
						{{ translate('address') }}
					</span>
					<button
						v-if="!requiredPhone && !requiredRecipientId"
						class="btn btn-sm btn-success float-right"
						@click="goToDetails()">
						{{ translate('order_details') }}
						<span class="fa fa-eye" />
					</button>
				</template>
				<b-row>
					<b-col
						v-if="!isAddressEditable"
						cols="12">
						<p>
							<span class="text-muted">
								<strong>
									{{ translate('order_data_has_changed') }}:
								</strong>
								<br>
								{{ `${getOrderValue('address')} ${getOrderValue('address2')}, ${getOrderValue('city')}, ${getOrderValue('region').name}, ${getOrderValue('postcode')}, ${translate(countryCode.toLowerCase())}` }}
							</span>
						</p>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isAddressEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('address') }) }}:
								</strong>
								<br>
								{{ getOrderValue('address') }}
							</span>
						</p>
						<label for="address">
							<template v-if="isAddressEditable">
								{{ translate('provide_new_value', { value: translate('address')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('address') }) }}:
								</strong>
							</template>
						</label>
						<b-input
							id="address"
							v-model="form.address"
							:required="true"
							:class="hasError('address') ? 'is-invalid' : ''"
							:disabled="!isAddressEditable"
							:placeholder="translate('address')"
							:setter-value="form.address"
							type="text"
							autocomplete="address-line1"
							@input="clearError('address')" />
						<template v-if="hasError('address')">
							<span
								v-for="error in errors['address']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isAddressEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('country') }) }}:
								</strong>
								<br>
								{{ translate(countryCode.toLowerCase()) }}
							</span>
						</p>
						<label for="country">
							<template v-if="isAddressEditable">
								{{ translate('country') }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('country') }) }}:
								</strong>
							</template>
						</label>
						<select
							id="country_code"
							v-model="form.country_code"
							:disabled="true"
							:class="hasError('country_code') ? 'is-invalid' : ''"
							name="country_code"
							autocomplete="off"
							class="form-control">
							<template>
								<option :value="countryCode">
									{{ translate(countryCode.toLowerCase()) }}
								</option>
							</template>
						</select>
						<template v-if="hasError('country_code')">
							<span
								v-for="error in errors['country_code']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isAddressEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('state') }) }}:
								</strong>
								<br>
								{{ getOrderValue('region').name }}
							</span>
						</p>
						<label for="region_id">
							<template v-if="isAddressEditable">
								{{ translate('provide_new_value', { value: translate('state')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('state') }) }}:
								</strong>
							</template>
						</label>
						<select
							id="region_id"
							v-model="form.region_id"
							:class="hasError('region_id') ? 'is-invalid' : ''"
							:disabled="!isAddressEditable"
							name="region_id"
							autocomplete="off"
							class="form-control"
							@change="handleRegionChange">
							<option value="">
								{{ translate('select_state') }}
							</option>
							<option
								v-for="state in states"
								:key="state.id"
								:value="state.id">
								{{ state.attributes.name }}
							</option>
						</select>
						<template v-if="hasError('region_id')">
							<span
								v-for="error in errors['region_id']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
				</b-row>
				<br>
				<b-row>
					<b-col
						cols="12"
						md="4">
						<p v-if="isAddressEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('city') }) }}:
								</strong>
								<br>
								{{ getOrderValue('city') }}
							</span>
						</p>
						<label for="address">
							<template v-if="isAddressEditable">
								{{ translate('provide_new_value', { value: translate('city')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('city') }) }}:
								</strong>
							</template>
						</label>
						<select
							id="city"
							v-model="form.city"
							:class="hasError('city') ? 'is-invalid' : ''"
							name="city"
							autocomplete="off"
							class="form-control"
							:disabled="citiesLoading || !isAddressEditable"
							@change="handleCityChange">
							<option value="">
								{{ translate('select_city') }}
							</option>
							<option
								v-for="city in cities"
								:key="city.id"
								:value="city.attributes.name">
								{{ city.attributes.name }}
							</option>
						</select>
						<template v-if="hasError('city')">
							<span
								v-for="error in errors['city']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isAddressEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('district') }) }}:
								</strong>
								<br>
								{{ getOrderValue('address2') }}
							</span>
						</p>
						<label for="country">
							<template v-if="isAddressEditable">
								{{ translate('district') }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('district') }) }}:
								</strong>
							</template>
						</label>
						<select
							id="address2"
							v-model="form.address2"
							:class="hasError('address2') ? 'is-invalid' : ''"
							name="address2"
							autocomplete="off"
							class="form-control"
							:disabled="districtsLoading || citiesLoading || form.is_pickup_address || !isAddressEditable"
							@change="handleDistrictChange">
							<option value="">
								{{ translate('select_district') }}
							</option>
							<option
								v-for="district in districts"
								:key="district.id"
								:value="district.attributes.name">
								{{ district.attributes.name }}
							</option>
						</select>
						<template v-if="hasError('address2')">
							<span
								v-for="error in errors['address2']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
					<b-col
						cols="12"
						md="4">
						<p v-if="isAddressEditable">
							<span class="text-muted">
								<strong>
									{{ translate('value_registered_in_order', { value: translate('postcode') }) }}:
								</strong>
								<br>
								{{ getOrderValue('postcode') }}
							</span>
						</p>
						<label for="region_id">
							<template v-if="isAddressEditable">
								{{ translate('provide_new_value', { value: translate('postcode')}) }}
								<span class="text-danger">*</span>
							</template>
							<template v-else>
								<strong class="text-muted">
									{{ translate('updated_value', { value: translate('postcode') }) }}:
								</strong>
							</template>
						</label>
						<b-input
							:id="'postcode'"
							v-model="form.postcode"
							:required="true"
							:class="hasError('postcode') ? 'is-invalid' : ''"
							:placeholder="translate('postcode')"
							:setter-value="form.postcode"
							type="text"
							autocomplete="postal-code"
							disabled
							@dataChanged="form.postcode = $event" />
						<template v-if="hasError('postcode')">
							<span
								v-for="error in errors['postcode']"
								:key="error"
								class="custom-invalid-feedback animated fadeIn"
								v-text="error" />
						</template>
					</b-col>
				</b-row>
			</b-card>
			<b-row>
				<b-col
					cols="12"
					class="text-right">
					<b-button
						v-if="isEditable"
						variant="primary"
						:disabled="updating"
						@click="confirmChange">
						{{ translate('save') }}
						<em
							v-if="updating"
							class="fa fa-spinner fa-spin" />
					</b-button>
				</b-col>
			</b-row>
		</template>
		<is-loading
			:loading-label="translate('loading')"
			:no-data-label="translate('data_not_found')"
			:loading="loading"
			:has-data="hasData && !loading" />
	</div>
</template>
<script>

import { VueTelInput } from 'vue-tel-input';
import {
	Addresses, Countries, DuplicatedOrders,
	Grids, NIN, OrdersDetails,
} from '@/translations';
import Order from '@/util/Order';
import ImageViewer from '@/components/ImageViewer';
import { IMAGES_EXT } from '@/settings/Media';
import { UNPROCESSABLE } from '@/settings/Errors';
import Country from '@/util/Country';

export default {
	name: 'OrderDuplicatedInfo',
	messages: [
		Grids, NIN, Addresses, DuplicatedOrders, OrdersDetails, Countries,
	],
	components: {
		ImageViewer,
		VueTelInput,
	},
	props: {
		orderId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			alert: new this.$Alert(),
			orderInfo: new Order(),
			updateInfo: new Order(),
			documents: new Order(),
			stateData: new Country(),
			stateCities: new Country(),
			cityDistricts: new Country(),
			errors: {},
			IMAGES_EXT,
			newFrontFile: null,
			newBackFile: null,
			userImageFront: '',
			userImageBack: '',
			currentImage: '',
			showImageViewer: false,
			loadingDocs: false,
			form: {
				mobile_number: '',
				personal_document_number: null,
				first_name: null,
				last_name: null,
				address: '',
				address2: '',
				region_id: '',
				city: '',
				country_code: '',
			},
			bindProps: {
				inputOptions: {
					showDialCode: true,
				},
				disabledFetchingCountry: false,
				dropdownOptions: {
					disabled: true,
				},
			},
		};
	},
	computed: {
		orderData() {
			try {
				const { data } = this.orderInfo.data.response.data;
				return data;
			} catch (error) {
				return {};
			}
		},
		hasData() {
			return Object.keys(this.orderData).length > 0;
		},
		loading() {
			return !!this.orderInfo.data.loading;
		},
		updating() {
			return !!this.updateInfo.data.loading;
		},
		isEditable() {
			try {
				return !this.orderData.attributes.completed;
			} catch (error) {
				return false;
			}
		},
		isPhoneEditable() {
			try {
				return !this.orderData.attributes.completed_telephone;
			} catch (error) {
				return false;
			}
		},
		isIdEditable() {
			try {
				return !this.orderData.attributes.completed_id_number;
			} catch (error) {
				return false;
			}
		},
		isAddressEditable() {
			try {
				return !this.orderData.attributes.completed_address;
			} catch (error) {
				return false;
			}
		},
		requiredPhone() {
			try {
				return this.orderData.attributes.required_telephone;
			} catch (error) {
				return false;
			}
		},
		requiredRecipientId() {
			try {
				return this.orderData.attributes.required_id_number;
			} catch (error) {
				return false;
			}
		},
		requiredAddress() {
			try {
				return this.orderData.attributes.required_address;
			} catch (error) {
				return false;
			}
		},
		uploadedValues() {
			try {
				return this.orderData.attributes.duplicate_order_details;
			} catch (error) {
				return {};
			}
		},
		orderValues() {
			try {
				return this.orderData.attributes.order_details;
			} catch (error) {
				return {};
			}
		},
		countryCode() {
			return this.getOrderValue('country').code;
		},
		states() {
			try {
				return this.stateData.data.response.data.data;
			} catch (error) {
				return [];
			}
		},
		citiesLoading() {
			return !!this.stateCities.data.loading;
		},
		districtsLoading() {
			return !!this.cityDistricts.data.loading;
		},
		cities() {
			try {
				return this.stateCities.data.response.data.data;
			} catch (e) {
				return [];
			}
		},
		districts() {
			try {
				return this.cityDistricts.data.response.data.data;
			} catch (e) {
				return [];
			}
		},
	},
	watch: {
		orderId(newId, oldId) {
			if (newId !== oldId) {
				this.errors = {};
				this.getOrderInfo();
			}
		},
	},
	mounted() {
		this.getOrderInfo();
	},
	methods: {
		getOrderInfo() {
			this.clearForm();
			this.orderInfo.getOrderDuplicatedInfo(this.orderId).then(() => {
				this.stateData.getStates(this.countryCode);
				this.form.country_code = this.countryCode;
				if (this.hasData) {
					if (!this.isPhoneEditable) {
						this.form.mobile_number = this.getValue('telephone');
					}

					if (!this.isIdEditable) {
						this.form.personal_document_number = this.getValue('recipient_id_number');
						this.form.first_name = this.getValue('first_name');
						this.form.last_name = this.getValue('last_name');

						if (this.form.personal_document_number) {
							this.fetchUserImage('front');
							this.fetchUserImage('back');
						}
					}

					if (!this.isAddressEditable) {
						this.form.address = this.getValue('address');
						this.form.postcode = this.getValue('postcode');
						this.form.country_code = this.getValue('country_code');
						this.form.region_id = String(this.getValue('region_id'));

						this.stateCities.getCities(this.getValue('region_id')).then(() => {
							const cityName = this.getValue('city');
							this.form.city = cityName;
							const cityId = this.cities.find((city) => city.attributes.name === cityName).id;
							this.cityDistricts.getDistricts(cityId).then(() => {
								this.form.address2 = this.getValue('address2');
							});
						});
					}
				}
			});
		},
		getOrderValue(field) {
			if (['recipient_id_number'].includes(field)) {
				return this.orderValues[field];
			}
			return this.orderValues.shipping[field];
		},
		getValue(field) {
			return this.uploadedValues[field];
		},
		confirmChange() {
			const trans = {
				title: this.translate('duplicated_order_confirmation_title'),
				text: this.translate('duplicated_order_confirmation_message'),
			};
			const options = {
				confirmButtonText: this.translate('confirm'),
				cancelButtonText: this.translate('cancel'),
			};
			this.alert.confirmation(trans.title, trans.text, options, true).then(() => {
				this.updateOrderInfo();
			});
		},
		updateOrderInfo() {
			if (this.newFrontFile) {
				this.form.personal_document_front_file = this.newFrontFile;
			}
			if (this.newBackFile) {
				this.form.personal_document_back_file = this.newBackFile;
			}
			const payload = this.objectToFormData(this.form);

			if (payload) {
				this.updateInfo.updateDuplicatedInfo(this.orderId, payload).then((response) => {
					const { message } = response.response;
					this.alert.toast('success', message, { timer: 6000 });
					this.$emit('orderCompleted');
					this.getOrderInfo();
				}).catch((errors) => {
					if (!UNPROCESSABLE.includes(this.updateInfo.data.errors.status)) {
						const { messageError } = errors;
						this.alert.toast('error', messageError, { timer: 6000 });
					} else {
						this.errors = this.updateInfo.data.errors.errors;
					}
				});
			}
		},
		hasError(field) {
			if (typeof this.errors[field] !== 'undefined') { return true; }
			return false;
		},
		clearError(field) {
			if (field) {
				delete this.errors[field];
				this.errors = { ...this.errors };
			}
		},
		clearForm() {
			this.form = {
				mobile_number: '',
				personal_document_number: null,
				first_name: null,
				last_name: null,
				address: '',
				address2: '',
				region_id: '',
				city: '',
				country_code: '',
			};
		},
		fetchUserImage(side) {
			this.loadingDocs = true;
			this.documents.getDuplicatedDocument(this.orderId, side).then(() => {
				const { response } = this.documents.data;
				const url = window.URL.createObjectURL(response.data);

				if (side === 'back') {
					this.newBackFile = null; // response.data;
					this.userImageBack = url;
				} else {
					this.newFrontFile = null; // response.data;
					this.userImageFront = url;
				}
			}).finally(() => {
				this.loadingDocs = false;
			});
		},
		goToDetails() {
			this.$router.push({
				name: 'OrdersDetails',
				params: {
					order_id: this.orderId,
				},
			});
		},
		handleRegionChange(event) {
			this.stateCities.getCities(event.target.value);
			this.form.city = '';
			this.form.address2 = '';
			this.form.postcode = '';
		},
		handleCityChange(event) {
			const cityName = event.target.value;
			this.form.address2 = '';
			this.form.postcode = '';
			if (cityName) {
				const cityId = this.cities.find((c) => c.attributes.name === event.target.value).id;
				this.cityDistricts.getDistricts(cityId);
			}
		},
		handleDistrictChange(event) {
			const districtName = event.target.value;
			this.form.postcode = '';
			this.clearError(`${this.prefixErrors}postcode`);
			if (districtName) {
				const district = this.districts.find((d) => d.attributes.name === event.target.value);
				this.form.postcode = district.attributes.postal_code;
			}
		},
	},
};

</script>
<style scoped>
.vue-tel-input.disabled {
	background-color: #e4e7ea;
}
</style>
